<template>
  <div class="security-my-documents">
    <h2 class="subheading grey--text">Mis Solicitudes de Documentos</h2>
    <v-container class="my-5">
      <v-alert v-if="securityError" type="error" dismissible class="mb-3">
        {{ securityError }}
      </v-alert>

      <v-layout v-if="isLoading" align-center justify-center row>
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-layout>

      <v-layout v-else-if="myDocumentRequests.length === 0" align-center justify-center row class="mt-5">
        <v-flex xs12 text-xs-center>
          <v-icon size="80" color="grey lighten-2" class="mb-3">description</v-icon>
          <h3 class="grey--text">No tienes solicitudes pendientes</h3>
          <p class="grey--text mt-2">Los documentos solicitados aparecerán aquí</p>
        </v-flex>
      </v-layout>

      <v-layout v-else row wrap>
        <v-flex xs12 sm6 md4 v-for="doc in myDocumentRequests" :key="doc.id" class="mb-4">
          <v-card class="doc-card elevation-2">
            <div class="doc-card-bar" :class="'bar-' + doc.status.toLowerCase()"></div>

            <v-card-text class="doc-card-body">
              <div class="doc-card-header">
                <h3 class="doc-title">{{ doc.document_type }}</h3>
                <v-chip small :color="getStatusColor(doc.status)" text-color="white">
                  {{ doc.status_display || doc.status }}
                </v-chip>
              </div>

              <p class="doc-description grey--text">{{ doc.description || 'Sin descripción' }}</p>

              <v-alert
                v-if="doc.status === 'REJECTED'"
                type="error"
                :value="true"
                icon="info"
                class="doc-rejection"
              >
                {{ doc.rejection_reason }}
              </v-alert>

              <div class="doc-meta">
                <div class="doc-meta-row">
                  <v-icon small color="grey darken-1">event</v-icon>
                  <span>{{ doc.deadline ? formatDate(doc.deadline) : 'Sin fecha límite' }}</span>
                </div>
                <v-chip
                  v-if="doc.deadline && (doc.status === 'PENDING' || doc.status === 'REJECTED')"
                  x-small
                  outline
                  :color="daysRemainingColor(doc.deadline)"
                >
                  {{ daysRemainingLabel(doc.deadline) }}
                </v-chip>
              </div>
            </v-card-text>

            <v-card-actions class="doc-card-actions">
              <v-spacer></v-spacer>
              <v-btn
                v-if="doc.status === 'PENDING' || doc.status === 'REJECTED'"
                small
                color="primary"
                @click="openSubmitDialog(doc)"
              >
                <v-icon left small>upload_file</v-icon>
                {{ doc.status === 'REJECTED' ? 'Reenviar' : 'Entregar' }}
              </v-btn>
              <v-icon v-else color="green">check_circle</v-icon>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- DIALOG: Entregar Documento -->
    <v-dialog v-model="showSubmitDialog" max-width="600px" persistent>
      <v-card v-if="selectedDocument" class="elevation-5">
        <v-toolbar dark color="primary">
          <v-toolbar-title>Entregar Documento</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon color="white" flat @click="closeSubmitDialog" class="align-self-end">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-layout row wrap>
            <v-flex xs12>
              <p><strong>Tipo de Documento:</strong> {{ selectedDocument.document_type }}</p>
              <p><strong>Descripción:</strong> {{ selectedDocument.description || 'Sin descripción' }}</p>
            </v-flex>

            <v-flex xs12>
              <v-divider class="my-3"></v-divider>
              <p><strong>¿Cómo deseas entregar el documento?</strong></p>
            </v-flex>

            <!-- Radio buttons para elegir método -->
            <v-flex xs12>
              <v-radio-group v-model="submitMethod">
                <v-radio label="URL Externa (Google Drive, OneDrive, etc.)" value="url"></v-radio>
                <v-radio label="Subir archivo directamente" value="upload"></v-radio>
              </v-radio-group>
            </v-flex>

            <!-- Opción 1: URL Externa -->
            <v-flex xs12 v-if="submitMethod === 'url'">
              <v-text-field
                v-model="fileUrl"
                label="URL del Archivo *"
                type="url"
                placeholder="https://drive.google.com/file/d/..."
                :rules="[v => !!v || 'URL es requerida']"
                hint="Pega el enlace compartido del archivo"
              ></v-text-field>
              <p class="grey--text caption">
                <v-icon small>info</v-icon>
                Asegúrate que el enlace sea accesible (no requiera contraseña)
              </p>
            </v-flex>

            <!-- Opción 2: Subir Archivo -->
            <v-flex xs12 v-if="submitMethod === 'upload'">
              <v-file-input
                v-model="fileUpload"
                label="Seleccionar Archivo *"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                @change="onFileSelected"
                :rules="[v => !!v || 'Archivo es requerido']"
                hint="Formatos aceptados: PDF, JPG, PNG, DOC, DOCX"
              ></v-file-input>
            </v-flex>

            <!-- Preview si se seleccionó archivo -->
            <v-flex xs12 v-if="fileUpload">
              <p class="grey--text">
                <strong>Archivo:</strong> {{ fileUpload.name }} ({{ formatFileSize(fileUpload.size) }})
              </p>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeSubmitDialog">Cancelar</v-btn>
          <v-btn
            color="primary"
            @click="submitDocument"
            :loading="isLoading"
            :disabled="!canSubmit"
          >
            Entregar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  FETCH_MY_DOCUMENT_REQUESTS,
  SUBMIT_DOCUMENT
} from '@/store/modules/security'
import {
  getStatusColor,
  formatRequestDate,
  daysRemainingLabel,
  daysRemainingColor
} from '@/utils/documentRequestHelpers'

export default {
  name: 'security_my_documents',
  computed: {
    ...mapGetters('security', {
      isLoading: 'isSecurityLoading',
      securityError: 'getSecurityError',
      myDocumentRequests: 'getMyDocumentRequests'
    }),
    canSubmit() {
      return this.submitMethod === 'url' ? !!this.fileUrl : !!this.fileUpload
    }
  },
  data() {
    return {
      showSubmitDialog: false,
      selectedDocument: null,
      submitMethod: 'url',
      fileUrl: '',
      fileUpload: null
    }
  },
  mounted() {
    this.fetchMyDocuments()
  },
  methods: {
    ...mapActions('security', [
      FETCH_MY_DOCUMENT_REQUESTS,
      SUBMIT_DOCUMENT
    ]),
    getStatusColor,
    formatDate: formatRequestDate,
    daysRemainingLabel,
    daysRemainingColor,
    fetchMyDocuments() {
      const token = this.$store.getters.getToken
      this[FETCH_MY_DOCUMENT_REQUESTS](token)
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || 'Error al cargar documentos')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    openSubmitDialog(doc) {
      this.selectedDocument = doc
      this.submitMethod = 'url'
      this.fileUrl = ''
      this.fileUpload = null
      this.showSubmitDialog = true
    },
    closeSubmitDialog() {
      this.showSubmitDialog = false
      this.selectedDocument = null
      this.fileUrl = ''
      this.fileUpload = null
      this.submitMethod = 'url'
    },
    submitDocument() {
      if (!this.canSubmit) {
        this.$store.commit('snackbar_change_message', 'Por favor proporciona un archivo o URL')
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
        return
      }

      const token = this.$store.getters.getToken
      this[SUBMIT_DOCUMENT]({
        token,
        submissionId: this.selectedDocument.id,
        fileUrl: this.submitMethod === 'url' ? this.fileUrl : null,
        fileUpload: this.submitMethod === 'upload' ? this.fileUpload : null
      })
        .then(() => {
          this.$store.commit('snackbar_change_message', 'Documento entregado correctamente')
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          this.closeSubmitDialog()
          this.fetchMyDocuments()
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || 'Error al entregar documento')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    onFileSelected() {
      // El evento ya actualiza fileUpload automáticamente
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.security-my-documents {
  padding: 20px;
}

.subheading {
  font-size: 24px;
  font-weight: 300;
}

.doc-card {
  height: 100%;
  min-height: 230px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
}

.doc-card-bar {
  height: 5px;
  width: 100%;
  flex-shrink: 0;
}

.bar-pending { background-color: #1976d2; }
.bar-approved { background-color: #43a047; }
.bar-rejected { background-color: #e53935; }
.bar-expired { background-color: #9e9e9e; }
.bar-completed { background-color: #43a047; }

.doc-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
}

.doc-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.doc-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  line-height: 1.3;
}

.doc-description {
  font-size: 13px;
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.doc-rejection {
  font-size: 12px;
  padding: 8px 12px;
  margin-bottom: 12px;
}

.doc-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
  padding-top: 8px;
}

.doc-meta-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}

.doc-card-actions {
  padding: 4px 16px 16px;
}
</style>
